import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useChartSize } from '../useChartSize';
import * as d3 from 'd3';
import { COLORS, MARGINS, PANEL_HEIGHT } from './constants';
import type { EarningsPoint } from './prepareEarnings';

/** Which pair of fields this panel draws. */
export type Measure = 'eps' | 'revenue';

interface Props {
  title: string;
  measure: Measure;
  points: EarningsPoint[];
  /** Renders a value for the axis, tooltip and labels. */
  format: (n: number) => string;
}

interface Hover {
  point: EarningsPoint;
  /** Pixel position of the hovered bar's centre, for tooltip placement. */
  x: number;
}

/**
 * Whether this series' floor sits far enough above zero that a zero-anchored
 * axis would crush every bar into the top of the panel.
 *
 * Derived rather than stored: it is a pure function of the data, and setting
 * it as state from the draw effect cost a second render on every pass.
 */
function liftsBaseline(points: EarningsPoint[], measure: Measure): boolean {
  const values = points.flatMap((p) =>
    [actualOf(p, measure), estimateOf(p, measure)].filter((v): v is number => v != null)
  );
  if (!values.length) return false;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return lo > 0 && lo / hi > 0.55;
}

function actualOf(p: EarningsPoint, m: Measure): number | null {
  return m === 'eps' ? p.epsActual : p.revenueActual;
}

function estimateOf(p: EarningsPoint, m: Measure): number | null {
  return m === 'eps' ? p.epsEstimate : p.revenueEstimate;
}

function surpriseOf(p: EarningsPoint, m: Measure): number | null {
  return m === 'eps' ? p.epsSurprise : p.revenueSurprise;
}

/** Fractional surprise as a signed percentage string. */
function pct(n: number): string {
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}%`;
}

/**
 * One measure's history: actual as a bar, consensus as a line over the top.
 *
 * Bars and line rather than paired bars because beat/miss then reads as pure
 * geometry — the bar clears the line or it does not — so the colour is a
 * second cue rather than the only one. That matters here: red/blue alone is
 * exactly the encoding colour-blind readers cannot use.
 *
 * Rendered twice per tab, once per measure. The two panels deliberately do NOT
 * share a y-scale — EPS is single-digit dollars against revenue in billions,
 * and one axis carrying both would flatten EPS into the baseline.
 */
export function EarningsChart({ title, measure, points, format }: Props): React.ReactElement {
  const { ref: wrapRef, width } = useChartSize<HTMLDivElement>();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  useEffect(() => {
    if (!svgRef.current || width <= 0 || !points.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = Math.max(0, width - MARGINS.left - MARGINS.right);
    const innerH = PANEL_HEIGHT - MARGINS.top - MARGINS.bottom;
    const g = svg.append('g').attr('transform', `translate(${MARGINS.left},${MARGINS.top})`);

    const x = d3
      .scaleBand<string>()
      .domain(points.map((p) => p.date))
      .range([0, innerW])
      .padding(0.28);

    // The domain spans actuals AND estimates so the consensus line can never
    // run off the top of the panel, and is padded so the tallest bar does not
    // touch the frame.
    const values = points.flatMap((p) =>
      [actualOf(p, measure), estimateOf(p, measure)].filter((v): v is number => v != null)
    );
    const dataMin = d3.min(values) ?? 0;
    const dataMax = d3.max(values) ?? 1;

    // Bars are read by length, so the baseline is zero by default. The
    // exception is a series whose floor sits far above zero — revenue in the
    // tens of billions — where a zero baseline crushes every quarter into the
    // top sliver of the panel and hides the variation the chart exists to
    // show. There the axis is allowed to start below the smallest value, and
    // `baselineLifted` flags it so the panel can say so.
    const baselineLifted = liftsBaseline(points, measure);
    const lo = baselineLifted ? dataMin - (dataMax - dataMin) * 0.55 : Math.min(0, dataMin);
    const pad = (dataMax - lo) * 0.12 || 1;

    const y = d3
      .scaleLinear()
      .domain([lo === 0 ? 0 : lo, dataMax + pad])
      .nice()
      .range([innerH, 0]);

    // ── Grid, drawn first so every mark sits above it ──────────────────
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(() => ''))
      .call((sel) => sel.select('.domain').remove())
      .selectAll('line')
      .attr('stroke', COLORS.grid);

    // ── Zero line, only when the data actually crosses it ──────────────
    if (y.domain()[0] < 0) {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', COLORS.axis)
        .attr('stroke-width', 1);
    }

    // ── Axes ───────────────────────────────────────────────────────────
    g.append('g')
      .call(d3.axisLeft(y).ticks(4).tickFormat((d) => format(d as number)))
      .call((sel) => sel.select('.domain').remove())
      .call((sel) => sel.selectAll('line').remove())
      .selectAll('text')
      .attr('fill', COLORS.text)
      .style('font-size', '10px');

    // Label thinning is driven by available WIDTH, not bar count: twelve bars
    // fit comfortably on a desktop panel and collide on a phone. A "Q3 '25"
    // label needs ~34px, so drop every Nth until each survivor has room.
    const LABEL_PX = 34;
    const labelStep = Math.max(1, Math.ceil(LABEL_PX / Math.max(1, innerW / points.length)));
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(x)
          .tickFormat((_d, i) => (i % labelStep === 0 ? points[i].label : ""))
          .tickSize(0)
      )
      .call((sel) => sel.select('.domain').remove())
      .selectAll('text')
      .attr('fill', COLORS.text)
      .attr('dy', '1.1em')
      .style('font-size', '10px');

    // Bars grow from zero when zero is on the panel, otherwise from the axis
    // floor — a lifted baseline has no zero to anchor to.
    const baseline = y(Math.max(y.domain()[0], Math.min(0, y.domain()[1])));

    // ── Bars: the reported actual ──────────────────────────────────────
    const reported = points.filter((p) => p.reported && actualOf(p, measure) != null);

    g.selectAll('.bar')
      .data(reported)
      .join('path')
      .attr('class', 'bar')
      .attr('d', (p) => {
        const v = actualOf(p, measure) as number;
        const bx = x(p.date) ?? 0;
        const bw = x.bandwidth();
        const top = y(Math.max(v, 0));
        const h = Math.abs(y(v) - baseline);
        // 4px rounded data-end: the corners away from the baseline are the
        // rounded pair, so a negative bar rounds downward.
        const r = Math.min(4, bw / 2, h);
        return v >= 0
          ? `M${bx},${top + h} L${bx},${top + r} Q${bx},${top} ${bx + r},${top} L${bx + bw - r},${top} Q${bx + bw},${top} ${bx + bw},${top + r} L${bx + bw},${top + h} Z`
          : `M${bx},${baseline} L${bx},${baseline + h - r} Q${bx},${baseline + h} ${bx + r},${baseline + h} L${bx + bw - r},${baseline + h} Q${bx + bw},${baseline + h} ${bx + bw},${baseline + h - r} L${bx + bw},${baseline} Z`;
      })
      .attr('fill', (p) => {
        const s = surpriseOf(p, measure);
        // No estimate means no verdict. Muted rather than neutral-bright: at
        // full value the unknown quarter was the lightest mark on the panel,
        // so the bar we know least about shouted the loudest.
        if (s == null) return COLORS.unknown;
        return s >= 0 ? COLORS.beat : COLORS.miss;
      })
      .attr('opacity', (p) => (hover && hover.point.date !== p.date ? 0.45 : 1));

    // ── Ghost bar for the scheduled quarter ────────────────────────────
    const upcoming = points.filter((p) => !p.reported && estimateOf(p, measure) != null);

    g.selectAll('.ghost')
      .data(upcoming)
      .join('rect')
      .attr('class', 'ghost')
      .attr('x', (p) => x(p.date) ?? 0)
      .attr('width', x.bandwidth())
      .attr('y', (p) => y(estimateOf(p, measure) as number))
      .attr('height', (p) => Math.abs(baseline - y(estimateOf(p, measure) as number)))
      .attr('rx', 3)
      .attr('fill', 'none')
      .attr('stroke', COLORS.upcoming)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3 3')
      .attr('opacity', 0.7);

    // ── Consensus line, over the bars ──────────────────────────────────
    const withEstimate = points.filter((p) => estimateOf(p, measure) != null);

    if (withEstimate.length > 1) {
      const line = d3
        .line<EarningsPoint>()
        .x((p) => (x(p.date) ?? 0) + x.bandwidth() / 2)
        .y((p) => y(estimateOf(p, measure) as number));

      // Drawn twice: a surface-coloured casing under the stroke keeps the line
      // legible where it crosses a filled bar of similar lightness.
      g.append('path')
        .datum(withEstimate)
        .attr('fill', 'none')
        .attr('stroke', COLORS.surface)
        .attr('stroke-width', 5)
        .attr('stroke-linejoin', 'round')
        .attr('opacity', 0.85)
        .attr('d', line);

      g.append('path')
        .datum(withEstimate)
        .attr('fill', 'none')
        .attr('stroke', COLORS.estimate)
        .attr('stroke-width', 2)
        .attr('stroke-linejoin', 'round')
        .attr('d', line);
    }

    g.selectAll('.est-dot')
      .data(withEstimate)
      .join('circle')
      .attr('class', 'est-dot')
      .attr('cx', (p) => (x(p.date) ?? 0) + x.bandwidth() / 2)
      .attr('cy', (p) => y(estimateOf(p, measure) as number))
      .attr('r', 4)
      .attr('fill', (p) => (p.reported ? COLORS.estimate : COLORS.surface))
      .attr('stroke', (p) => (p.reported ? COLORS.surface : COLORS.upcoming))
      .attr('stroke-width', 2);

    // ── Hover targets: full-height columns, so the mouse never has to find
    //    a thin bar. Transparent, and above every mark. ─────────────────
    g.selectAll('.hit')
      .data(points)
      .join('rect')
      .attr('class', 'hit')
      .attr('x', (p) => x(p.date) ?? 0)
      .attr('y', 0)
      .attr('width', x.bandwidth())
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mouseenter', (_e, p) =>
        setHover({ point: p, x: (x(p.date) ?? 0) + x.bandwidth() / 2 + MARGINS.left })
      )
      .on('mouseleave', () => setHover(null));
  }, [points, width, measure, format, hover]);

  const lifted = useMemo(() => liftsBaseline(points, measure), [points, measure]);
  const h = hover?.point;
  const hActual = h ? actualOf(h, measure) : null;
  const hEstimate = h ? estimateOf(h, measure) : null;
  const hSurprise = h ? surpriseOf(h, measure) : null;

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">{title}</h3>
        <div className="flex items-center gap-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLORS.beat }} />
            Beat
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLORS.miss }} />
            Miss
          </span>
          <span className="flex items-center gap-1">
            <svg width="16" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="16" y2="4" stroke={COLORS.estimate} strokeWidth="2" />
              <circle cx="8" cy="4" r="3" fill={COLORS.estimate} />
            </svg>
            Estimate
          </span>
        </div>
      </div>

      <svg ref={svgRef} width={width} height={PANEL_HEIGHT} role="img" aria-label={`${title} history`} />

      {lifted && (
        <div className="mt-0.5 text-[10px] text-gray-500">
          Axis starts above zero — compare bar tops, not lengths.
        </div>
      )}

      {h && (
        <div
          className="pointer-events-none absolute top-6 z-10 rounded border border-dark-border bg-dark-bg/95 px-2 py-1.5 text-[11px] shadow-lg"
          style={{
            // Flip to the left of the cursor near the right edge so the card
            // never runs outside the panel.
            left: hover && hover.x > width - 140 ? undefined : hover?.x,
            right: hover && hover.x > width - 140 ? width - (hover?.x ?? 0) : undefined,
            transform: hover && hover.x > width - 140 ? 'translateX(0)' : 'translateX(-50%)',
          }}
        >
          <div className="mb-1 font-semibold text-gray-200">
            {h.label} <span className="text-gray-500">{h.date}</span>
          </div>
          <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 font-numeric">
            <span className="text-gray-400">Actual</span>
            <span className="text-right text-white">{hActual != null ? format(hActual) : '—'}</span>
            <span className="text-gray-400">Estimate</span>
            <span className="text-right text-gray-300">{hEstimate != null ? format(hEstimate) : '—'}</span>
            <span className="text-gray-400">Surprise</span>
            <span
              className="text-right font-semibold"
              style={{ color: hSurprise == null ? COLORS.text : hSurprise >= 0 ? COLORS.beat : COLORS.miss }}
            >
              {hSurprise != null ? pct(hSurprise) : h.reported ? '—' : 'Scheduled'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
